import { formatDate } from '@/lib/date-time';
/**
 * @file routes/_app/dispatch/index.tsx
 * @description 派案專區列表頁（M1）。
 *
 * 功能：
 * - 狀態 Select：受理中 / 合約已簽 / 已報價 / 已收費 / 已派案 / 稽核中 / 報告中 / 結案 / NC整改中
 * - Table 顯示案件列表（caseCode, clientName, clientCode/驗證註冊碼, verificationType, workflowState, dueDate）
 * - 「新建案件」按鈕 → 導航到 /dispatch/new
 * - 點擊行 → 導航到案件詳情 /dispatch/:caseId
 *
 * 角色：
 * - manager/admin 可見所有案件，「新建案件」按鈕可用
 * - auditor 僅見自己的案件，無「新建案件」按鈕
 *
 * 視覺：採用 ListPageShell + FilterBar + StatusPill 體系。
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Input, Popconfirm, Select, Space, Table, Tooltip } from 'antd';
import { Pencil, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { userCan } from '@zhcb/shared';
import { ApiError, apiClient } from '@/lib/api-client';
import { message } from '@/lib/antd-app';
import { useAuthStore } from '@/lib/auth-context';
import {
  ListPageShell,
  FilterBar,
  FilterBarLabel,
  StatusPill,
  type StatusPillProps,
} from '@/components/shell';
import { hideBelow } from '@/components/common/responsive-columns';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/dispatch/')({ component: DispatchListPage });

// ─── 類型定義 ─────────────────────────────────────────────────────────────────

/**
 * 案件列表項類型（來自 /api/cases 分頁列表）。
 */
interface CaseItem {
  id: number;
  caseCode: string;
  clientId: number;
  clientName: string | null;
  /** 客戶驗證註冊碼（clientCode，例 ZH-TAP-0001），永久編號 */
  clientCode: string | null;
  /** 稽核計劃編號（派案時生成；未派案為 null，列表回落顯示案件編號，序91） */
  planCode: string | null;
  scheme: string;
  verificationType: string;
  workflowState: string;
  assignedTeamLeadId: number | null;
  /** 主導稽核員（稽核組長）姓名，序173 派案列表新增欄位 */
  teamLeadName: string | null;
  dispatchedAt: string | null;
  dueDate: string | null;
  createdAt: string;
}

/**
 * 分頁列表 API 響應。
 */
interface ListResponse {
  items: CaseItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ─── 狀態顏色映射 ─────────────────────────────────────────────────────────────

const STATE_TONE: Record<string, StatusPillProps['tone']> = {
  受理建檔: 'info',
  合約已簽: 'brand',
  已報價: 'brand',
  已收費: 'warning',
  已派案: 'success',
  已繳費: 'success', // 兼容旧数据
  稽核中: 'warning',
  報告中: 'brand',
  報告評審: 'warning',
  決定中: 'info',
  已發證: 'success',
  已寄送: 'success',
  結案: 'muted',
  NC整改中: 'danger',
};

// ─── 組件 ────────────────────────────────────────────────────────────────────

/**
 * 派案專區列表頁。
 */
function DispatchListPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user!);
  const isManager = user.roles.includes('admin') || user.roles.includes('manager');
  /** 序23：刪除案件改由獨立能力 case.delete 控制（可於角色權限管理勾選） */
  const canDeleteCase = userCan(user, 'case.delete');

  /** 狀態過濾 */
  const [stateFilter, setStateFilter] = useState<string>('');
  /** 搜尋關鍵字 */
  const [search, setSearch] = useState('');
  /** 序127：主導稽核員篩選（管理組多維查詢；僅 isManager 可用） */
  const [leadAuditorId, setLeadAuditorId] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  /** 主導稽核員下拉選項（管理組才拉取，復用員工檔案端點） */
  const { data: auditorsData } = useQuery<{ items: { id: number; name: string }[] }>({
    queryKey: ['dispatch-lead-auditors'],
    queryFn: () => apiClient.get('/api/settings/staff?pageSize=200&role=auditor'),
    enabled: isManager,
  });

  /** 查詢案件列表 */
  const { data, isLoading, refetch } = useQuery<ListResponse>({
    queryKey: ['cases', stateFilter, search, leadAuditorId, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (stateFilter) params.set('state', stateFilter);
      if (search) params.set('search', search);
      if (leadAuditorId != null) params.set('leadAuditorId', String(leadAuditorId));
      return apiClient.get(`/api/cases?${params.toString()}`);
    },
  });

  const listData = data;

  const queryClient = useQueryClient();

  /**
   * 軟刪除案件 mutation。
   * 任何 workflowState 皆可刪，刪除後寫入回收站可還原（2026-08-21 需求）。
   * 行級權限由後端把關：管理組可刪任何案件，其餘人僅可刪自己建立的。
   * 成功後使列表查詢失效以刷新；失敗按狀態碼提示。
   */
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/cases/${id}`),
    onSuccess: () => {
      message.success('案件已刪除，可在回收站還原');
      void queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
    onError: (err: unknown) => {
      // 403 FORBIDDEN：無權刪除此案件（非管理組且非本人建立）
      if (err instanceof ApiError && err.status === 403) {
        message.error('僅管理組或案件建立者可刪除此案件');
        return;
      }
      message.error(err instanceof ApiError ? err.message : '刪除失敗，請稍後再試');
    },
  });

  /** 表格列定義 */
  const columns: ColumnsType<CaseItem> = [
    {
      // 序91：案件編號 改顯示 計劃編號（派案時生成的稽核計劃編號）；未派案回落案件編號
      title: '計劃編號',
      dataIndex: 'planCode',
      width: 160,
      render: (_: string | null, record) => (
        <Button
          type="link"
          size="small"
          style={{ fontFamily: 'monospace', padding: 0 }}
          onClick={() =>
            void navigate({ to: '/dispatch/$caseId', params: { caseId: String(record.id) } })
          }
        >
          {record.planCode ?? record.caseCode}
        </Button>
      ),
    },
    {
      title: '客戶名稱',
      dataIndex: 'clientName',
      ellipsis: true,
      render: (name: string | null) => name ?? '—',
    },
    {
      // 驗證註冊碼 = 客戶 clientCode（例 ZH-TAP-0001），永久編號
      title: '驗證註冊碼',
      dataIndex: 'clientCode',
      width: 140,
      render: (code: string | null) =>
        code ? <span style={{ fontFamily: 'monospace' }}>{code}</span> : '—',
    },
    { ...hideBelow('lg'), title: '驗證方案', dataIndex: 'scheme', width: 110 },
    { ...hideBelow('lg'), title: '驗證類型', dataIndex: 'verificationType', width: 120 },
    {
      // 序173：派案專區列表新增【稽核組長】欄，顯示主導稽核員姓名
      ...hideBelow('lg'),
      title: '稽核組長',
      dataIndex: 'teamLeadName',
      width: 110,
      render: (name: string | null) => name ?? '未指派',
    },
    {
      title: '狀態',
      dataIndex: 'workflowState',
      width: 120,
      render: (state: string) => (
        <StatusPill tone={STATE_TONE[state] ?? 'neutral'} dot size="sm">
          {state}
        </StatusPill>
      ),
    },
    {
      title: '到期日',
      dataIndex: 'dueDate',
      width: 110,
      render: (d: string | null) => {
        if (!d) return '—';
        const dt = new Date(d);
        const isUrgent = dt.getTime() - Date.now() < 7 * 86400000;
        return (
          <span style={{ color: isUrgent ? '#e11d48' : undefined }}>
            {formatDate(dt)}
          </span>
        );
      },
    },
    {
      ...hideBelow('xl'),
      title: '建立時間',
      dataIndex: 'createdAt',
      width: 120,
      render: (d: string) => formatDate(d),
    },
  ];

  // manager/admin 專屬：修改 + 刪除操作列（序 55）。
  // - 修改：跳轉案件詳情頁的「修改案件」抽屜（詳情頁承載完整編輯表單）。
  // - 刪除：刪除按鈕**一直可用**（不再受 workflowState 限制），刪除後可在回收站還原。
  //   權限（行級）由後端最終把關：管理組可刪任何案件，其餘人僅可刪自己建立的（2026-08-21 需求）。
  //   2026-09-15 回報序23：刪除按鈕改由獨立能力 case.delete 控制（角色權限管理可勾選），
  //   修改按鈕仍限管理組（isManager）。
  //   非「受理建檔」草稿態屬正式流程案件，二次確認文案加強警示，避免誤刪。
  if (isManager || canDeleteCase) {
    columns.push({
      title: '操作',
      key: 'actions',
      width: 110,
      fixed: 'right',
      align: 'center',
      render: (_: unknown, record: CaseItem) => {
        // 是否為草稿態：僅影響二次確認文案的警示強度，不影響是否可刪
        const isDraft = record.workflowState === '受理建檔';
        const confirmDescription = isDraft
          ? `確定刪除「${record.caseCode}」？可在回收站還原。`
          : `此案件已進入「${record.workflowState}」流程，確定刪除「${record.caseCode}」？刪除後可在回收站還原。`;
        return (
          // 阻止冒泡，避免觸發整行的「進入詳情」導航
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
            <Space size={0}>
              {isManager && (
                <Tooltip title="修改案件">
                  <Button
                    type="text"
                    size="small"
                    icon={<Pencil size={14} />}
                    aria-label="修改案件"
                    onClick={() =>
                      void navigate({
                        to: '/dispatch/$caseId',
                        params: { caseId: String(record.id) },
                      })
                    }
                  />
                </Tooltip>
              )}
              {canDeleteCase && (
                <Popconfirm
                  title="刪除案件"
                  description={confirmDescription}
                  okText="刪除"
                  cancelText="取消"
                  okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
                  onConfirm={() => deleteMutation.mutate(record.id)}
                >
                  <Tooltip title="刪除案件">
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<Trash2 size={14} />}
                      aria-label="刪除案件"
                    />
                  </Tooltip>
                </Popconfirm>
              )}
            </Space>
          </div>
        );
      },
    });
  }

  return (
    <ListPageShell
      title="派案專區"
      description="管理所有驗證案件的受理、派案與繳費流程"
      actions={
        isManager ? (
          <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => void navigate({ to: '/dispatch/new' })}
          >
            新建案件
          </Button>
        ) : undefined
      }
      filters={
        <FilterBar
          search={
            <Input
              prefix={<Search size={14} />}
              placeholder="搜尋案件編號 / 客戶名稱…"
              value={search}
              allowClear
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          }
          filters={
            <>
              <FilterBarLabel>狀態</FilterBarLabel>
              <Select
                allowClear
                placeholder="全部狀態"
                value={stateFilter || undefined}
                onChange={(v) => {
                  setStateFilter(v ?? '');
                  setPage(1);
                }}
                style={{ width: 160 }}
                options={[
                  { value: '受理建檔', label: '受理中' },
                  { value: '合約已簽', label: '合約已簽' },
                  { value: '已報價', label: '已報價' },
                  { value: '已收費', label: '已收費' },
                  { value: '已派案', label: '已派案' },
                  { value: '稽核中', label: '稽核中' },
                  { value: '報告中', label: '報告中' },
                  { value: '結案', label: '結案' },
                  { value: 'NC整改中', label: 'NC整改中' },
                ]}
              />
              {/* 序127：主導稽核員多維篩選（管理組可選定某稽核員查看其全部案件） */}
              {isManager && (
                <>
                  <FilterBarLabel>主導稽核員</FilterBarLabel>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="全部稽核員"
                    value={leadAuditorId}
                    onChange={(v) => {
                      setLeadAuditorId(v ?? undefined);
                      setPage(1);
                    }}
                    style={{ width: 180 }}
                    options={(auditorsData?.items ?? []).map((a) => ({
                      value: a.id,
                      label: a.name,
                    }))}
                  />
                </>
              )}
            </>
          }
          extra={
            <Button icon={<RefreshCw size={14} />} onClick={() => void refetch()}>
              刷新
            </Button>
          }
        />
      }
    >
      {/* 案件表格（小螢幕保留橫向滾動兜底） */}
      <Table<CaseItem>
        dataSource={listData?.items ?? []}
        columns={columns}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize,
          total: listData?.total ?? 0,
          onChange: setPage,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        onRow={(record) => ({
          onClick: () =>
            void navigate({ to: '/dispatch/$caseId', params: { caseId: String(record.id) } }),
          style: { cursor: 'pointer' },
        })}
      />
    </ListPageShell>
  );
}
